import {
  Alert,
  Descriptions,
  Empty,
  Space,
  Tag as AntdTag,
  Timeline,
  Tooltip,
} from "antd";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  IOrderOperationTimingPhase,
  IOrderOperationTimings,
  OrderOperationTimingPhaseKey,
} from "../../../models/order/response";
import {
  formatOperationSeconds,
  getOperationPhaseDuration,
  getOperationPhaseSeconds,
  hasOperationTimingNumber,
} from "../../../utils/helpers/order-operation-timings";
import OrderOperationDuration from "../order-operation-duration";
import styles from "./style.module.scss";

interface IProps {
  timings?: IOrderOperationTimings | null;
}

const PHASES: Array<{ key: OrderOperationTimingPhaseKey; label: string }> = [
  { key: "storeAcceptance", label: "store_acceptance_phase" },
  { key: "preparing", label: "preparing_time_stats" },
  {
    key: "customerAcceptance",
    label: "modified_order_acceptance_time_stats",
  },
  {
    key: "accountingFinalization",
    label: "accounting_finalization_time_stats",
  },
  { key: "driverSearchToPickup", label: "driver_search_to_pickup" },
  { key: "delivery", label: "area_delivery_time_stats" },
];

const formatDate = (value?: string | null) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "-";

const isSameDateTime = (first?: string | null, second?: string | null) => {
  if (!first || !second) return false;

  const firstDate = moment(first);
  const secondDate = moment(second);

  if (!firstDate.isValid() || !secondDate.isValid()) {
    return first === second;
  }

  return firstDate.isSame(secondDate);
};

const getTimingDate = (
  timings: IOrderOperationTimings,
  camelKey: keyof IOrderOperationTimings,
  snakeKey: string,
) => ((timings as any)?.[camelKey] ?? (timings as any)?.[snakeKey]) as
  | string
  | null
  | undefined;

const getPhaseLabel = (
  phaseKey?: string | null,
  fallback = "-",
): string => {
  if (!phaseKey) return fallback;
  return PHASES.find((phase) => phase.key === phaseKey)?.label ?? fallback;
};

const hasPhasePayload = (
  phase?: IOrderOperationTimingPhase,
  duration?: unknown,
  seconds?: number | null,
) =>
  !!phase?.startedAt ||
  !!phase?.endedAt ||
  phase?.isActive ||
  !!duration ||
  hasOperationTimingNumber(seconds);

const OrderOperationTimingsPanel: React.FC<IProps> = ({ timings }) => {
  const { t } = useTranslation();
  const secondLabel = t("seconds");

  const dataQuality = timings?.dataQuality ?? (timings as any)?.data_quality;
  const repeatedStatusesSource =
    dataQuality?.repeatedStatuses ?? dataQuality?.repeated_statuses;
  const repeatedStatuses = Array.isArray(repeatedStatusesSource)
    ? repeatedStatusesSource
    : [];
  const stageSumGreaterThanTotal =
    timings?.stageSumGreaterThanTotal ||
    (timings as any)?.stage_sum_greater_than_total ||
    dataQuality?.stageSumGreaterThanTotal ||
    dataQuality?.stage_sum_greater_than_total;
  const hasUnattributedSeconds =
    hasOperationTimingNumber(timings?.unattributedSeconds) &&
    Number(timings?.unattributedSeconds) > 0;
  const hasIncompleteCoverage =
    hasOperationTimingNumber(timings?.stageCoverageRate) &&
    Number(timings?.stageCoverageRate) < 100;
  const qualityWarnings = useMemo(() => {
    if (
      !dataQuality &&
      !stageSumGreaterThanTotal &&
      !hasUnattributedSeconds &&
      !hasIncompleteCoverage
    ) {
      return [];
    }

    return [
      dataQuality?.missingCycleStart || dataQuality?.missing_cycle_start
        ? t("missing_cycle_start_warning")
        : undefined,
      dataQuality?.missingCompletedAt || dataQuality?.missing_completed_at
        ? t("missing_completed_at_warning")
        : undefined,
      repeatedStatuses.length > 0
        ? t("repeated_statuses_warning", {
            count: repeatedStatuses.length,
          })
        : undefined,
      stageSumGreaterThanTotal
        ? t("stage_sum_greater_than_total_warning")
        : undefined,
      hasUnattributedSeconds || hasIncompleteCoverage
        ? t("operation_timings_unattributed_warning")
        : undefined,
    ].filter(Boolean) as string[];
  }, [
    dataQuality,
    hasIncompleteCoverage,
    hasUnattributedSeconds,
    repeatedStatuses.length,
    stageSumGreaterThanTotal,
    t,
  ]);

  if (!timings) {
    return <Empty description={t("no_operation_timings")} />;
  }

  const cycleStartedAt = getTimingDate(
    timings,
    "cycleStartedAt",
    "cycle_started_at",
  );
  const completedAt = getTimingDate(timings, "completedAt", "completed_at");
  const finishedAt = getTimingDate(timings, "finishedAt", "finished_at");
  const measuredUntilAt = getTimingDate(
    timings,
    "measuredUntilAt",
    "measured_until_at",
  );
  const shouldShowFinishedAt =
    !!finishedAt && !isSameDateTime(finishedAt, completedAt);
  const shouldShowMeasuredUntilAt = !timings.isCompleted && !!measuredUntilAt;
  const shouldShowCurrentPhase =
    !!timings.currentPhase ||
    !!timings.currentPhaseElapsedDuration ||
    hasOperationTimingNumber(timings.currentPhaseElapsedSeconds);
  const shouldShowUnattributed =
    hasOperationTimingNumber(timings.unattributedSeconds) &&
    timings.unattributedSeconds > 0;
  const phaseItems = PHASES.reduce<any[]>((items, { key, label }) => {
    const phase = timings.phases?.[key];
    const duration = getOperationPhaseDuration(phase);
    const seconds = getOperationPhaseSeconds(phase);
    const shouldHideOptionalPhase =
      (key === "customerAcceptance" || key === "accountingFinalization") &&
      !hasPhasePayload(phase, duration, seconds);

    if (shouldHideOptionalPhase) return items;

    const durationTag = (
      <AntdTag color={phase?.isActive ? "processing" : "default"}>
        <OrderOperationDuration duration={duration} seconds={seconds} />
      </AntdTag>
    );

    items.push({
      color: phase?.isActive
        ? "blue"
        : !!duration || hasOperationTimingNumber(seconds)
          ? "green"
          : "gray",
      children: (
        <div className={styles.timelineContent}>
          <div className={styles.phaseTitle}>
            <span>{t(label)}</span>
            {hasOperationTimingNumber(seconds) ? (
              <Tooltip
                title={`${t("actual_duration")}: ${formatOperationSeconds(
                  seconds,
                  secondLabel,
                )}`}
              >
                {durationTag}
              </Tooltip>
            ) : (
              durationTag
            )}
            {phase?.isActive && <AntdTag color="blue">{t("active")}</AntdTag>}
          </div>
          <div className={styles.phaseMeta}>
            <span>
              {t("started_at")}: {formatDate(phase?.startedAt)}
            </span>
            <span>
              {t("ended_at")}: {formatDate(phase?.endedAt)}
            </span>
          </div>
        </div>
      ),
    });

    return items;
  }, []);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {qualityWarnings.length > 0 && (
        <Alert
          className={styles.qualityAlert}
          type="warning"
          showIcon
          message={t("operation_timings_data_quality_warning")}
          description={
            <ul className={styles.qualityList}>
              {qualityWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}

      <Descriptions size="small" bordered column={2}>
        <Descriptions.Item label={t("cycle_started_at")}>
          {formatDate(cycleStartedAt)}
        </Descriptions.Item>
        {completedAt && (
          <Descriptions.Item label={t("completed_at")}>
            {formatDate(completedAt)}
          </Descriptions.Item>
        )}
        {shouldShowFinishedAt && (
          <Descriptions.Item label={t("finished_at")}>
            {formatDate(finishedAt)}
          </Descriptions.Item>
        )}
        {shouldShowMeasuredUntilAt && (
          <Descriptions.Item label={t("measured_until_at")}>
            <Tooltip title={t("measured_until_at_tooltip")}>
              <span>{formatDate(measuredUntilAt)}</span>
            </Tooltip>
          </Descriptions.Item>
        )}
        {(!!timings.totalCompletedDuration ||
          hasOperationTimingNumber(timings.totalCompletedSeconds)) && (
          <Descriptions.Item label={t("total_completed_minutes")}>
            <OrderOperationDuration
              duration={timings.totalCompletedDuration}
              seconds={timings.totalCompletedSeconds}
            />
          </Descriptions.Item>
        )}
        {shouldShowCurrentPhase && (
          <>
            <Descriptions.Item label={t("current_phase")}>
              {timings.currentPhase ? t(getPhaseLabel(timings.currentPhase)) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("current_phase_elapsed_minutes")}>
              <OrderOperationDuration
                duration={timings.currentPhaseElapsedDuration}
                seconds={timings.currentPhaseElapsedSeconds}
              />
            </Descriptions.Item>
          </>
        )}
        {shouldShowUnattributed && (
          <Descriptions.Item label={t("unattributed_minutes")}>
            <Tooltip title={t("phase_breakdown_unattributed_tooltip")}>
              <span>
                <OrderOperationDuration
                  duration={timings.unattributedDuration}
                  seconds={timings.unattributedSeconds}
                />
              </span>
            </Tooltip>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("order_terminal_state")}>
          <Space size="small">
            <AntdTag color={timings.isCompleted ? "green" : "default"}>
              {timings.isCompleted ? t("completed") : t("not_completed")}
            </AntdTag>
            <AntdTag color={timings.isTerminal ? "blue" : "default"}>
              {timings.isTerminal ? t("terminal") : t("not_terminal")}
            </AntdTag>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Timeline items={phaseItems} />
    </Space>
  );
};

export default OrderOperationTimingsPanel;
